/* global zxcvbn, alert */

import React, { useEffect, useState, useRef, FormEvent } from 'react';
import { Link, useLocation } from 'react-router';
import { connect } from 'react-redux';
import { user, stroot } from '@birdi/js-sdk';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import BirdiLogoSVG from '@birdi/icons/birdi-logo-navy.svg';
import OnboardingCreateAccountSVG from '@birdi/icons/onboarding-create-account-2.svg';
import PersonIcon from '@birdi/icons/onboarding-user.svg';
import EmailIcon from '@birdi/icons/onboarding-at.svg';
import LockIcon from '@birdi/icons/onboarding-lock.svg';
import { GoogleAuthButton } from './google-auth-button';
import ErrorDisplay from '../../common/ErrorDisplay';
import { updateField } from '../actions';
import VerifyYourEmail from './verify-your-email';
import { Length } from '../../common/password/Length';
import { LoadZXCVBN, ZXCVBNDisplay } from '../../common/password/zxcvbn';
import { saveAuthSearchParams } from '../utils';
import { useMetaSelector } from '../../meta/reducer';
import { ProgressBar } from './progress-bar';
import * as styles from './styles';
import { AuthState } from '../reducer';

interface SignUpProps {
  auth: AuthState;
  _updateField: (field: Partial<AuthState>) => void;
  setLeftSideImage?: (image: string) => void;
}

const SignUp: React.FC<SignUpProps> = (props) => {
  const theme = useTheme() as BirdiTheme;
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [zxcvbnResult, setZxcvbnResult] = useState<any>(null);
  const [error, setError] = useState<any>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isEmailFromStripe, setIsEmailFromStripe] = useState(false);
  const meta = useMetaSelector();
  const { setLeftSideImage } = props;

  const formRef = useRef<HTMLFormElement>(null);

  // Change left side image when showing verify email screen
  useEffect(() => {
    if (props.auth.awaitingEmailVerification && setLeftSideImage) {
      setLeftSideImage('satellite-stockpiles');
    }
  }, [props.auth.awaitingEmailVerification, setLeftSideImage]);

  useEffect(() => {
    // Initialize with existing auth state if available
    const { auth } = props;
    if (auth.fname && auth.lname) {
      setFullName(`${auth.fname} ${auth.lname}`.trim());
    }
    setPassword('');
    setConfirmPassword('');
    props._updateField({ awaitingEmailVerification: false });
    saveAuthSearchParams();

    // Check if organisationId is in URL and fetch email from Stripe
    const searchParams = new URLSearchParams(location.search);
    const organisationId = searchParams.get('organisationId');
    if (organisationId) {
      user
        .getSignupEmail(stroot('lishu0'), organisationId)
        .then((res: any) => {
          if (res.body?.email) {
            // Pre-fill email field with Stripe customer email and disable editing
            props._updateField({ email: res.body.email });
            setIsEmailFromStripe(true);
          }
        })
        .catch((err) => {
          // Silently fail - if email can't be retrieved, user can still enter it manually
          console.log('Could not fetch email from Stripe:', err);
        });
    }
  }, [location.search]);

  const setPasswordValue = (value: string) => {
    const { auth } = props;
    if (typeof (window as any).zxcvbn === 'function') {
      const zxcvbnResultValue = (window as any).zxcvbn(value, [auth.email]);
      setZxcvbnResult(zxcvbnResultValue);
    }
    setPassword(value);
  };

  const handleFullNameChange = (value: string) => {
    setFullName(value);
    // Split full name into first and last name
    const parts = value.trim().split(/\s+/);
    const fname = parts[0] || '';
    const lname = parts.slice(1).join(' ') || '';
    props._updateField({ fname, lname });
  };

  const handleSignUp = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { _updateField, auth } = props;

    if (!formRef.current?.checkValidity()) {
      return formRef.current?.reportValidity();
    }

    if (!termsAccepted) {
      return alert('Please accept the Terms of Service and Privacy Policy');
    }

    if (password !== confirmPassword) {
      return alert('Passwords do not match');
    }

    if (zxcvbnResult && zxcvbnResult.score < 1) {
      return alert(
        `The password you're using is too weak. ${zxcvbnResult.feedback.suggestions[0]}`,
      );
    }

    // Read organisationId from URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const organisationId = searchParams.get('organisationId');

    const userObj = {
      email: auth.email,
      fname: auth.fname,
      lname: auth.lname,
      password,
      // Add organisationId if present (will be sent as query parameter)
      ...(organisationId && { organisationId }),
      // For accepting a mission share on sign up
      ...(meta.invitation && { invitationId: meta.invitation }),
    };

    return user
      .create(stroot('the7th'), Object.assign(userObj))
      .then(() =>
        _updateField({
          awaitingEmailVerification: true,
        }),
      )
      .catch((err) => setError(err));
  };

  if (props.auth.awaitingEmailVerification) {
    return <VerifyYourEmail />;
  }

  return (
    <>
      <LoadZXCVBN onLoad={() => {}} />

      {/* Logo - Fixed at top */}
      <div css={styles.logoContainer}>
        <BirdiLogoSVG />
      </div>

      {/* Scrollable Content */}
      <div css={styles.scrollableContent}>
        {/* Header Section */}
        <div css={styles.headerSection}>
          <div css={styles.iconContainer}>
            <div css={styles.iconWrapper(theme)}>
              <OnboardingCreateAccountSVG />
            </div>
            <h1 css={styles.title}>Create your Birdi account</h1>
          </div>

          <ProgressBar currentStep={1} totalSteps={6} />

          <p css={styles.subtitle}>
            Create your account to start your 7-day free trial with Birdi.
          </p>
        </div>

        {/* Form */}
        <form
          ref={formRef}
          onSubmit={handleSignUp}
          css={css`
            display: flex;
            flex-direction: column;
            gap: 0.25rem;

            @media (min-width: ${breakpoints.s}) {
              gap: 0.5rem;
            }
          `}
        >
          {/* Full Name */}
          <div css={styles.formField}>
            <label css={styles.label} htmlFor="fullName">
              Full name <span className="required">*</span>
            </label>
            <div css={styles.inputWrapper}>
              <div css={styles.inputIcon}>
                <PersonIcon />
              </div>
              <input
                id="fullName"
                type="text"
                css={styles.input}
                placeholder="Enter your full name"
                value={fullName}
                onChange={(event) => handleFullNameChange(event.target.value)}
                required
              />
            </div>
            <ErrorDisplay error={error} path="fname" />
            <ErrorDisplay error={error} path="lname" />
          </div>

          {/* Email */}
          <div css={styles.formField}>
            <label css={styles.label} htmlFor="email">
              Email address <span className="required">*</span>
            </label>
            <div css={styles.inputWrapper}>
              <div css={styles.inputIcon}>
                <EmailIcon />
              </div>
              <input
                id="email"
                type="email"
                css={styles.input}
                placeholder="Enter your email"
                value={props.auth.email}
                onChange={(event) =>
                  props._updateField({ email: event.target.value })
                }
                required
                disabled={isEmailFromStripe}
              />
            </div>
            <div
              css={(theme: BirdiTheme) => css`
                ${styles.errorMessage};
                a {
                  color: ${theme.deepOcean};
                  font-weight: 500;
                }
              `}
            >
              <ErrorDisplay error={error} path="email" />
            </div>
            {error?.body?.message === 'auth.alreadyExists' && (
              <div css={styles.errorMessage}>
                This email is already registered. Please sign in or use a
                different email address.
              </div>
            )}
          </div>

          {/* Password */}
          <div css={styles.formField}>
            <label css={styles.label} htmlFor="password">
              Password <span className="required">*</span>
            </label>
            <div css={styles.inputWrapper}>
              <div css={styles.inputIcon}>
                <LockIcon />
              </div>
              <input
                id="password"
                type="password"
                css={styles.input}
                placeholder="Create a password"
                value={password}
                onChange={(event) => setPasswordValue(event.target.value)}
                required
                minLength={8}
              />
            </div>
            <Length password={password} />
            <ZXCVBNDisplay zxcvbnResult={zxcvbnResult} />
          </div>

          {/* Confirm Password */}
          <div css={styles.formField}>
            <label css={styles.label} htmlFor="confirmPassword">
              Confirm password <span className="required">*</span>
            </label>
            <div css={styles.inputWrapper}>
              <div css={styles.inputIcon}>
                <LockIcon />
              </div>
              <input
                id="confirmPassword"
                type="password"
                css={styles.input}
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                minLength={8}
              />
            </div>
            {confirmPassword && password !== confirmPassword && (
              <div css={styles.errorMessage}>Passwords do not match</div>
            )}
          </div>

          {/* Terms Checkbox */}
          <div css={styles.checkboxContainer}>
            <input
              id="terms"
              type="checkbox"
              css={styles.checkbox}
              checked={termsAccepted}
              onChange={(event) => setTermsAccepted(event.target.checked)}
              required
            />
            <label css={styles.checkboxLabel} htmlFor="terms">
              I agree to the{' '}
              <a
                href="https://www.birdi.io/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="https://www.birdi.io/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
            </label>
          </div>

          <div
            css={(theme: BirdiTheme) => css`
              ${styles.errorMessage};
              a {
                color: ${theme.deepOcean};
                font-weight: 500;
              }
            `}
          >
            {error?.body?.message !== 'auth.alreadyExists' &&
              error?.body?.code !== 'auth.alreadyExists' && (
                <ErrorDisplay error={error} path="meta" />
              )}
          </div>

          {/* Buttons */}
          <div css={styles.buttonContainer}>
            <button type="submit" css={styles.primaryButton}>
              Create Account
            </button>
            <GoogleAuthButton label="Sign up with Google" />
          </div>

          {/* Sign In Link */}
          <div
            css={(theme: BirdiTheme) => css`
              text-align: left;
              margin-top: 0.5rem;
              font-size: 0.9375rem;
              color: ${theme.mono70};
              flex-shrink: 0;
            `}
          >
            Already have an account?{' '}
            <Link
              to="/sign-in"
              css={(theme: BirdiTheme) => css`
                color: ${theme.deepOcean};
                text-decoration: none;
                font-weight: 500;

                &:hover {
                  text-decoration: underline;
                }
              `}
            >
              Sign in
            </Link>
          </div>
        </form>
      </div>

      {/* Fixed Footer - Help Link */}
      <div css={styles.fixedFooter}>
        <div css={styles.helpLink}>
          Need help? <a href="mailto:support@birdi.io">Contact support</a>
        </div>
      </div>
    </>
  );
};

export default connect(
  (state: any) => ({
    auth: state.auth,
  }),
  (dispatch: any) => ({
    _updateField: (field: Partial<AuthState>) => dispatch(updateField(field)),
  }),
)(SignUp);
